import React from 'react';
import { CRow, CCol } from '@coreui/react-pro';
import { Modal } from 'react-bootstrap';
import { CXButton, CXTextInput } from '../../../components/CXForm';

type Props = {
    show: boolean;
    onHide: () => void;

    remarkText: string;
    setRemarkText: (v: string) => void;

    remarkFiles: File[];
    remarkUploading: boolean;
    remarkSaving: boolean;
    remarkError: string;
    remarkHasError: boolean;

    onRemarkFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemarkDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    onRemarkDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
    onRemoveFile: (index: number) => void;

    onUpload: () => Promise<void> | void;
};

export default function RemarkModal(props: Props) {
    const {
        show,
        onHide,
        remarkText,
        setRemarkText,
        remarkFiles,
        remarkUploading,
        remarkSaving,
        remarkError,
        remarkHasError,
        onRemarkFileChange,
        onRemarkDrop,
        onRemarkDragOver,
        onRemoveFile,
        onUpload,
    } = props;

    return (
        <Modal
            className="remarkModal"
            show={show}
            onHide={onHide}
            size="xl"
        >
            <Modal.Header className="modal-header" closeButton>
                <Modal.Title>{'Remarks'}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <CRow>
                    <CCol xs={6}>
                        <span
                            className="modal-section-title"
                            style={{
                                fontFamily: 'Cathay Sans',
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: '24px',
                                color: '#4C4C4C',
                            }}
                        >
                            Remarks
                        </span>
                    </CCol>

                    <CCol xs={6}>
                        <div className={remarkHasError ? 'errorBorder' : ''}>
                            <CXTextInput
                                placeholder={'Remarks'}
                                value={remarkText}
                                onChange={(value) => {
                                    setRemarkText(value);
                                }}
                                zIndex={1029}
                            />
                        </div>
                        {remarkError && (
                            <div
                                style={{
                                    color: '#DC3545',
                                    fontSize: 14,
                                    marginTop: 8,
                                    lineHeight: '18px',
                                }}
                            >
                                {remarkError}
                            </div>
                        )}

                        <div
                            onDrop={onRemarkDrop}
                            onDragOver={onRemarkDragOver}
                            style={{
                                border: '1px dashed #D8D8D8',
                                borderRadius: 4,
                                padding: '24px 16px',
                                textAlign: 'center',
                                marginTop: 16,
                                marginBottom: 16,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 14,
                                    lineHeight: '20px',
                                    color: '#4C4C4C',
                                    marginBottom: 12,
                                }}
                            >
                                Drop a file here to upload
                            </div>
                            <label
                                style={{
                                    display: 'inline-block',
                                    padding: '8px 16px',
                                    borderRadius: 4,
                                    border: '1px solid #116F9A',
                                    color: '#116F9A',
                                    fontSize: 14,
                                    cursor: 'pointer',
                                }}
                            >
                                Or choose files{' '}
                                <input
                                    type="file"
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={onRemarkFileChange}
                                    disabled={remarkUploading}
                                />
                            </label>
                        </div>

                        <div
                            style={{
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: '24px',
                                color: '#4C4C4C',
                                marginBottom: 4,
                            }}
                        >
                            Uploaded files {remarkUploading && '(uploading...)'}
                        </div>
                        {remarkFiles.length === 0 ? (
                            <div
                                style={{
                                    width: '100%',
                                    minHeight: 40,
                                    borderRadius: 4,
                                    border: '1px solid #E6E6E6',
                                    background: '#F5F7F8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0 12px',
                                    fontSize: 14,
                                    color: '#808285',
                                }}
                            >
                                No files uploaded
                            </div>
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    borderRadius: 4,
                                    border: '1px solid #E6E6E6',
                                    background: '#F5F7F8',
                                    padding: '8px 12px',
                                }}
                            >
                                {remarkFiles.map((file, index) => (
                                    <div
                                        key={file.name}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '4px 0',
                                            fontSize: 14,
                                            color: '#000',
                                        }}
                                    >
                                        <span
                                            style={{
                                                flex: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {file.name}
                                        </span>
                                        <button
                                            onClick={() => onRemoveFile(index)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#DC3545',
                                                cursor: 'pointer',
                                                fontSize: 14,
                                                marginLeft: 8,
                                            }}
                                            disabled={remarkUploading}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CCol>
                </CRow>
            </Modal.Body>

            <Modal.Footer className="modal-footer">
                <div
                    className="cancel-button"
                    style={{ bottom: '40px', right: '256px', width: '200px' }}
                >
                    <CXButton
                        outline={true}
                        text={'Cancel'}
                        onClick={onHide}
                        fontSize={'16px'}
                        width={'100%'}
                        disabled={remarkSaving}
                    />
                </div>
                <div
                    className="save-button"
                    style={{ bottom: '40px', right: '40px', width: '200px' }}
                >
                    <CXButton
                        text={remarkUploading || remarkSaving ? 'Uploading...' : 'Upload'}
                        onClick={onUpload}
                        fontSize={'16px'}
                        width={'100%'}
                        disabled={remarkUploading || remarkSaving}
                    />
                </div>
            </Modal.Footer>
        </Modal>
    );
}
